'use client'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { PanelLeft, PanelLeftClose } from 'lucide-react'
import {
  type ComponentProps,
  type CSSProperties,
  createContext,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Button } from './button'
import { cn } from './cn'
import { Input } from './input'
import { MOTION_DURATION } from './motion'
import { Skeleton } from './skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip'
import { DEFAULT_MOBILE_BREAKPOINT, useIsMobile } from './use-is-mobile'

/**
 * The pin: `true` only once a person pins the rail open. Renamed from
 * `sidebar_state` (an expanded-by-default era value) so every rail starts
 * collapsed; the web shell reads it server-side under the same name
 * (`components/layout/sidebar-pin.ts`).
 */
const SIDEBAR_COOKIE_NAME = 'sidebar_pinned'
/** One year, refreshed on every toggle: a pin is a lasting preference. */
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 365
/**
 * Exported so miniature replicas of the shell (the onboarding preview) derive
 * their sidebar width from the same source instead of hardcoding it. A shell
 * with different geometry overrides `--sidebar-width` through the
 * `SidebarProvider` `style` prop (the admin app derives it from its grid).
 */
export const SIDEBAR_WIDTH = '12rem'
/**
 * The off-canvas panel's default width; a shell overrides it through the
 * `SidebarProvider` `mobileWidth` prop (the admin app uses 18rem). A prop,
 * not a CSS variable: the panel is portaled to `document.body` and would not
 * inherit a variable set on the provider's wrapper.
 */
const SIDEBAR_WIDTH_MOBILE = '12rem'
const SIDEBAR_WIDTH_ICON = '3rem'
const SIDEBAR_KEYBOARD_SHORTCUT = 'b'
/**
 * `expandOnHover`: hover intent before the rail peeks open, so a cursor
 * crossing the rail on its way to the page never opens it. A close is never
 * delayed.
 */
const SIDEBAR_PEEK_OPEN_DELAY_MS = MOTION_DURATION.micro * 1000
/** The peek's `duration-quick` close: the icon layout lands once it is shut. */
const SIDEBAR_PEEK_CLOSE_MS = MOTION_DURATION.quick * 1000

type SidebarContextProps = {
  /** The pinned layout: what the page beside the rail is sized against. */
  state: 'expanded' | 'collapsed'
  open: boolean
  setOpen: (open: boolean) => void
  /** `expandOnHover` only; read the peek itself with `useSidebarPeeking`. */
  setPeeking: (peeking: boolean) => void
  expandOnHover: boolean
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  /** Width of the off-canvas panel (`mobileWidth` on the provider). */
  mobileWidth: string
  toggleSidebar: () => void
}

const SidebarContext = createContext<SidebarContextProps | null>(null)
/**
 * `expandOnHover`: whether the collapsed rail is hover-expanded OVER the page.
 * Its own context so a hover re-renders only what paints the rail's labels,
 * never every menu button or the page header. `state` stays `collapsed`
 * while peeking (nothing beside the rail moves), so a part that paints the
 * rail's contents reads `state === 'expanded' || useSidebarPeeking()`.
 */
const SidebarPeekContext = createContext(false)

function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.')
  }

  return context
}

function useOptionalSidebar() {
  return useContext(SidebarContext)
}

function useSidebarPeeking() {
  return useContext(SidebarPeekContext)
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  mobileBreakpoint = DEFAULT_MOBILE_BREAKPOINT,
  mobileWidth = SIDEBAR_WIDTH_MOBILE,
  expandOnHover = false,
  className,
  style,
  children,
  ...props
}: ComponentProps<'div'> & {
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Width of the off-canvas panel below `mobileBreakpoint` (web 12rem, admin 18rem). */
  mobileWidth?: string
  /**
   * Viewport width (px) below which the sidebar becomes the off-canvas
   * mobile panel and `SidebarTrigger` toggles it. The web shell uses the
   * default (`md`, 768); the admin shell's 12-column grid hides the rail
   * below `lg`, so it passes 1024.
   */
  mobileBreakpoint?: number
  /**
   * Desktop `collapsible="icon"` rail (web shell): collapsed is the resting
   * state and hovering the rail expands it OVER the page without moving it;
   * `open` (the toggle, ⌘B) pins it expanded beside the page. Rail tooltips
   * are off — hovering already shows every label.
   */
  expandOnHover?: boolean
}) {
  const isMobile = useIsMobile(mobileBreakpoint)
  const [isOpenMobile, setOpenMobile] = useState(false)
  const [isOpenInternal, setIsOpenInternal] = useState(defaultOpen)
  const [isPeekingInternal, setPeeking] = useState(false)
  const isOpen = openProp ?? isOpenInternal
  const isPeeking = expandOnHover && !isMobile && !isOpen && isPeekingInternal
  const setOpen = useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const isNextOpenState =
        typeof value === 'function' ? value(isOpen) : value
      if (setOpenProp) {
        setOpenProp(isNextOpenState)
      } else {
        setIsOpenInternal(isNextOpenState)
      }
      // Pinning absorbs a peek; unpinning under the cursor collapses the rail
      // until the pointer leaves and comes back.
      setPeeking(false)

      // biome-ignore lint/suspicious/noDocumentCookie: pre-Ultracite baseline
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${isNextOpenState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
    },
    [isOpen, setOpenProp]
  )

  const toggleSidebar = useCallback(
    () =>
      isMobile ? setOpenMobile((value) => !value) : setOpen((value) => !value),
    [isMobile, setOpen]
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault()
        toggleSidebar()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleSidebar])

  const state = isOpen ? 'expanded' : 'collapsed'

  const contextValue = useMemo<SidebarContextProps>(
    () => ({
      state,
      open: isOpen,
      setOpen,
      setPeeking,
      expandOnHover,
      isMobile,
      mobileWidth,
      openMobile: isOpenMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [
      state,
      isOpen,
      setOpen,
      expandOnHover,
      isMobile,
      mobileWidth,
      isOpenMobile,
      toggleSidebar,
    ]
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      <SidebarPeekContext.Provider value={isPeeking}>
        <TooltipProvider delayDuration={0}>
          <div
            data-slot="sidebar-wrapper"
            style={
              {
                '--sidebar-width': SIDEBAR_WIDTH,
                '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
                ...style,
              } as CSSProperties
            }
            className={cn(
              'group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar',
              className
            )}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarPeekContext.Provider>
    </SidebarContext.Provider>
  )
}

type PeekPointer = { x: number; y: number }

/** A popup the rail owns is open (its trigger, inside the rail, says so). */
function hasOpenPopup(container: HTMLElement): boolean {
  return container.querySelector('[aria-expanded="true"]') !== null
}

function hasKeyboardFocus(container: HTMLElement): boolean {
  const active = document.activeElement
  if (!(active && container.contains(active))) {
    return false
  }
  try {
    return active.matches(':focus-visible')
  } catch {
    return false
  }
}

function isPointerOver(container: HTMLElement, pointer: PeekPointer | null) {
  if (!pointer) {
    return false
  }
  const rect = container.getBoundingClientRect()
  return (
    pointer.x >= rect.left &&
    pointer.x < rect.right &&
    pointer.y >= rect.top &&
    pointer.y < rect.bottom
  )
}

/**
 * `expandOnHover`: drives `setPeeking` from the rail's container. A mouse
 * resting on the rail for the intent delay, or keyboard focus entering it,
 * opens the peek; it closes the moment none of these holds: the pointer is
 * over the rail's box (geometry, so a modal menu's `pointer-events: none` on
 * the body cannot fake a leave), keyboard focus is inside, or a popup the rail
 * owns is open (its menu sits outside the rail). Touch never peeks.
 */
function usePeekGestures(
  containerRef: RefObject<HTMLDivElement | null>,
  isEnabled: boolean,
  setPeeking: (peeking: boolean) => void
) {
  useEffect(() => {
    const container = containerRef.current
    if (!(isEnabled && container)) {
      return
    }
    let isOpen = false
    let openTimer: ReturnType<typeof setTimeout> | undefined
    let pointer: PeekPointer | null = null

    const open = () => {
      clearTimeout(openTimer)
      openTimer = undefined
      if (!isOpen) {
        isOpen = true
        setPeeking(true)
      }
    }
    const settle = () => {
      if (
        !isOpen ||
        isPointerOver(container, pointer) ||
        hasKeyboardFocus(container) ||
        hasOpenPopup(container)
      ) {
        return
      }
      isOpen = false
      setPeeking(false)
    }
    const handlePointerEnter = (event: PointerEvent) => {
      pointer = { x: event.clientX, y: event.clientY }
      if (event.pointerType !== 'mouse' || isOpen || openTimer) {
        return
      }
      openTimer = setTimeout(open, SIDEBAR_PEEK_OPEN_DELAY_MS)
    }
    const handlePointerLeave = (event: PointerEvent) => {
      pointer = { x: event.clientX, y: event.clientY }
      clearTimeout(openTimer)
      openTimer = undefined
      settle()
    }
    const handlePointerMove = (event: PointerEvent) => {
      pointer = { x: event.clientX, y: event.clientY }
      settle()
    }
    const handleFocusIn = () => {
      if (hasKeyboardFocus(container)) {
        open()
      }
    }
    // Focus lands on its next target after `focusout`; settle once it has.
    const handleFocusOut = () => queueMicrotask(settle)
    // A popup trigger flipping `aria-expanded` releases (or takes) the hold.
    const popupObserver = new MutationObserver(settle)

    container.addEventListener('pointerenter', handlePointerEnter)
    container.addEventListener('pointerleave', handlePointerLeave)
    container.addEventListener('focusin', handleFocusIn)
    container.addEventListener('focusout', handleFocusOut)
    document.addEventListener('pointermove', handlePointerMove, {
      capture: true,
      passive: true,
    })
    popupObserver.observe(container, {
      attributes: true,
      attributeFilter: ['aria-expanded'],
      subtree: true,
    })
    // Unpinned (⌘B) with keyboard focus already inside: no `focusin` will
    // come, so the labels stay painted from here. A mouse unpin leaves the
    // logo focused without `:focus-visible` and collapses as usual.
    if (hasKeyboardFocus(container)) {
      open()
    }
    return () => {
      clearTimeout(openTimer)
      container.removeEventListener('pointerenter', handlePointerEnter)
      container.removeEventListener('pointerleave', handlePointerLeave)
      container.removeEventListener('focusin', handleFocusIn)
      container.removeEventListener('focusout', handleFocusOut)
      document.removeEventListener('pointermove', handlePointerMove, {
        capture: true,
      })
      popupObserver.disconnect()
      if (isOpen) {
        setPeeking(false)
      }
    }
  }, [containerRef, isEnabled, setPeeking])
}

function Sidebar({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  ...props
}: ComponentProps<'div'> & {
  side?: 'left' | 'right'
  variant?: 'sidebar' | 'floating' | 'inset'
  collapsible?: 'offcanvas' | 'icon' | 'none'
}) {
  const {
    isMobile,
    mobileWidth,
    state,
    setPeeking,
    expandOnHover,
    openMobile: isOpenMobile,
    setOpenMobile,
  } = useSidebar()
  const isPeeking = useSidebarPeeking()

  const closeMobile = useCallback(() => {
    setOpenMobile(false)
  }, [setOpenMobile])

  // `expandOnHover` reveals the left icon rail by clipping, never by width:
  // the container takes the full width at once (the page beside it keeps the
  // rail's), and a `clip-path` inset opens over it — paint only, no layout
  // per frame. On close the expanded layout stays until the clip has shut
  // (`isClosing`), then the icon geometry lands under it.
  const isHoverRail =
    expandOnHover && collapsible === 'icon' && side === 'left' && !isMobile
  const isPinned = state === 'expanded'
  const isPainted = isPinned || isPeeking
  const containerRef = useRef<HTMLDivElement>(null)
  const [wasPainted, setWasPainted] = useState(isPainted)
  const [isClosing, setClosing] = useState(false)
  if (wasPainted !== isPainted) {
    setWasPainted(isPainted)
    setClosing(isHoverRail && !isPainted)
  }
  const isLayoutExpanded = isPainted || isClosing

  usePeekGestures(containerRef, isHoverRail && !isPinned, setPeeking)

  useEffect(() => {
    if (!isClosing) {
      return
    }
    const isMotionReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    const timer = setTimeout(
      () => setClosing(false),
      isMotionReduced ? 0 : SIDEBAR_PEEK_CLOSE_MS
    )
    return () => clearTimeout(timer)
  }, [isClosing])

  if (collapsible === 'none') {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          'flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground',
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }

  // The mobile panel is the web shell's contract: a fixed slide-in panel
  // behind a scrim button (closes on tap), not a Radix Sheet — no focus trap
  // or Escape handling. It is portaled to `document.body` so a shell may mount
  // `Sidebar` inside a column it hides below the breakpoint (the admin grid
  // does) without the panel inheriting that `display: none`. This branch only
  // renders after mount (`isMobile` is false during SSR), so the portal never
  // meets the server renderer.
  if (isMobile) {
    return createPortal(
      <>
        {/* Stays mounted so it fades out with the panel; inert while closed
            (out of the tab order and the accessibility tree). */}
        <button
          type="button"
          aria-label="Close sidebar"
          inert={!isOpenMobile}
          className={cn(
            'fixed inset-0 z-40 bg-black/20 transition-opacity ease-smooth-out',
            {
              'opacity-100 duration-slow': isOpenMobile,
              'pointer-events-none opacity-0 duration-medium': !isOpenMobile,
            }
          )}
          onClick={closeMobile}
        />
        <div
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex h-svh flex-col bg-sidebar text-sidebar-foreground transition-transform ease-smooth-out motion-reduce:transition-none',
            {
              'translate-x-0 duration-slow': isOpenMobile,
              '-translate-x-full duration-medium': !isOpenMobile,
            }
          )}
          style={
            {
              '--sidebar-width': mobileWidth,
              width: mobileWidth,
            } as CSSProperties
          }
          {...props}
        >
          <div className="flex h-full w-full flex-col">{children}</div>
        </div>
      </>,
      document.body
    )
  }

  if (isHoverRail) {
    return (
      <div
        className="group peer border-border border-r text-sidebar-foreground"
        data-state={state}
        data-collapsible={isLayoutExpanded ? '' : collapsible}
        data-peek={isPeeking ? 'open' : undefined}
        data-variant={variant}
        data-side={side}
        data-slot="sidebar"
      >
        {/* The page is sized against the pinned state only: a peek never
            moves it. Pin opens on `fast`, unpin closes on `quick`, in step
            with the clip. */}
        <div
          data-slot="sidebar-gap"
          className={cn(
            'relative bg-transparent transition-[width] ease-smooth-out',
            {
              'w-(--sidebar-width) duration-fast': isPinned,
              'w-(--sidebar-width-icon) duration-quick': !isPinned,
            }
          )}
        />
        {/* The container never takes a hit itself: while a close is still
            clipping shut it is wider than what shows, and a click beside the
            icon strip must reach the page. The clipped inner (clip-path also
            clips hit-testing) takes the pointer; enter/leave still reach the
            container through it. */}
        <div
          ref={containerRef}
          data-slot="sidebar-container"
          className={cn(
            'pointer-events-none fixed inset-y-0 left-0 z-50 flex h-svh',
            {
              'w-(--sidebar-width)': isLayoutExpanded,
              'w-(--sidebar-width-icon)': !isLayoutExpanded,
            },
            className
          )}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            data-slot="sidebar-inner"
            className={cn(
              'pointer-events-auto flex h-full w-full flex-col bg-sidebar transition-[clip-path,box-shadow] ease-smooth-out motion-reduce:transition-none',
              {
                // Open: a negative right inset lets the overlay's edge shadow
                // out. Shut: everything right of the icon rail is clipped.
                'duration-fast [clip-path:inset(0_-2rem_0_0)]': isPainted,
                'duration-quick [clip-path:inset(0_calc(100%_-_var(--sidebar-width-icon))_0_0)]':
                  !isPainted,
                // Over the page (peeking, or shutting after an unpin) the
                // rail draws its own edge; pinned, the in-flow border is it.
                'shadow-[1px_0_0_var(--color-border),8px_0_24px_-12px_hsl(0_0%_0%/0.18)]':
                  !isPinned && isLayoutExpanded,
              }
            )}
          >
            {children}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="group peer border-border border-r text-sidebar-foreground md:block"
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
    >
      <div
        data-slot="sidebar-gap"
        className={cn(
          'relative w-(--sidebar-width) bg-transparent transition-[width] duration-fast ease-smooth-out',
          'group-data-[collapsible=offcanvas]:w-0',
          'group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)'
        )}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          'fixed inset-y-0 z-50 h-svh w-(--sidebar-width) transition-[left,right,width] duration-fast ease-smooth-out md:flex',
          side === 'left'
            ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
            : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
          variant === 'floating' || variant === 'inset'
            ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=right]:border-l group-data-[side=left]:border-none',
          className
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-border group-data-[variant=floating]:shadow-sm"
        >
          {children}
        </div>
      </div>
    </div>
  )
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}: ComponentProps<typeof Button>) {
  const { toggleSidebar, state } = useSidebar()
  const isOpen = state === 'expanded'

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon"
      className={cn('size-7', className)}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      {isOpen ? <PanelLeftClose /> : <PanelLeft />}
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
}

function SidebarRail({ className, ...props }: ComponentProps<'button'>) {
  const { toggleSidebar } = useSidebar()

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        'absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition duration-fast ease-smooth-out after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] after:transition-colors after:duration-quick hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex',
        'in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize',
        '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'group-data-[collapsible=offcanvas]:translate-x-0 hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:after:left-full',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className
      )}
      {...props}
    />
  )
}

function SidebarInset({ className, ...props }: ComponentProps<'main'>) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        'relative flex w-full flex-1 flex-col bg-sidebar',
        'md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2 md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-lg md:peer-data-[variant=inset]:shadow-sm',
        className
      )}
      {...props}
    />
  )
}

function SidebarInput({ className, ...props }: ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn('h-8 w-full bg-background shadow-none', className)}
      {...props}
    />
  )
}

function SidebarHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn('flex flex-col gap-2 px-2', className)}
      {...props}
    />
  )
}

function SidebarFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  )
}

function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: ComponentProps<'button'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        'absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-lg p-0 text-sidebar-foreground outline-hidden ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        'after:absolute after:-inset-2 md:after:hidden',
        'group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  )
}

function SidebarContent({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
        className
      )}
      {...props}
    />
  )
}

function SidebarGroup({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn('relative flex w-full min-w-0 flex-col p-2', className)}
      {...props}
    />
  )
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: ComponentProps<'div'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'div'

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        'flex h-8 shrink-0 items-center rounded-lg px-2 font-medium text-sidebar-foreground/70 text-xs outline-hidden ring-sidebar-ring transition-[margin,opacity] duration-fast ease-smooth-out focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        'group-data-[collapsible=icon]:opacity-0',
        className
      )}
      {...props}
    />
  )
}

function SidebarGroupContent({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn('w-full text-sm', className)}
      {...props}
    />
  )
}

function SidebarMenu({ className, ...props }: ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn('flex w-full min-w-0 flex-col gap-1', className)}
      {...props}
    />
  )
}

function SidebarMenuItem({ className, ...props }: ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn('group/menu-item relative', className)}
      {...props}
    />
  )
}

const sidebarMenuButtonVariants = cva(
  cn(
    'peer/menu-button relative flex w-full items-center gap-3 overflow-hidden rounded-lg px-2 text-left text-sm outline-hidden ring-sidebar-ring',
    'transition-[color,background-color,border-color,box-shadow,width,padding,gap] duration-fast ease-smooth-out',
    'hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground',
    'focus-visible:ring-2',
    'active:bg-sidebar-accent active:text-sidebar-accent-foreground',
    'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
    'group-has-data-[sidebar=menu-action]/menu-item:pr-8',
    'data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground',
    'before:absolute before:top-1/2 before:left-0 before:h-4 before:w-[3px] before:-translate-y-1/2 before:rounded-full before:bg-foreground before:opacity-0 before:transition-opacity before:duration-quick',
    'data-[active=true]:before:opacity-100',
    'data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground',
    'group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:p-2',
    'group-data-[collapsible=icon]:before:hidden',
    '[&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0'
  ),
  {
    variants: {
      variant: {
        default:
          'hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground',
        outline:
          'border border-border bg-background hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
      },
      size: {
        default: 'h-8 text-sm',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ...props
}: ComponentProps<'button'> & {
  asChild?: boolean
  isActive?: boolean
  tooltip?: string | ComponentProps<typeof TooltipContent>
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  const Comp = asChild ? Slot : 'button'
  const { isMobile, state, expandOnHover } = useSidebar()

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  )

  if (!tooltip) {
    return button
  }

  const tooltipProps =
    typeof tooltip === 'string'
      ? {
          children: tooltip,
        }
      : tooltip

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== 'collapsed' || isMobile || expandOnHover}
        {...tooltipProps}
      />
    </Tooltip>
  )
}

function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: ComponentProps<'button'> & {
  asChild?: boolean
  showOnHover?: boolean
}) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        'absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-lg p-0 text-sidebar-foreground outline-hidden ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0',
        'after:absolute after:-inset-2 md:after:hidden',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        showOnHover &&
          'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0',
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuBadge({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        'pointer-events-none absolute right-1 flex h-5 min-w-5 select-none items-center justify-center rounded-sm px-1 font-medium text-sidebar-foreground text-xs tabular-nums',
        'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: ComponentProps<'div'> & {
  showIcon?: boolean
}) {
  const width = useMemo(() => '70%', [])

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn('flex h-8 items-center gap-2 rounded-lg px-2', className)}
      {...props}
    >
      {showIcon ? (
        <Skeleton
          className="size-4 rounded-lg"
          data-sidebar="menu-skeleton-icon"
        />
      ) : null}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as CSSProperties
        }
      />
    </div>
  )
}

function SidebarMenuSub({ className, ...props }: ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        'mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-border border-l px-2.5 py-0.5',
        'group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuSubItem({ className, ...props }: ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn('group/menu-sub-item relative', className)}
      {...props}
    />
  )
}

function SidebarMenuSubButton({
  asChild = false,
  size = 'md',
  isActive = false,
  className,
  ...props
}: ComponentProps<'a'> & {
  asChild?: boolean
  size?: 'sm' | 'md'
  isActive?: boolean
}) {
  const Comp = asChild ? Slot : 'a'

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        'flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-lg px-2 text-sidebar-foreground outline-hidden ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground',
        'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
        size === 'sm' && 'text-xs',
        size === 'md' && 'text-sm',
        'group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  )
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useOptionalSidebar,
  useSidebar,
  useSidebarPeeking,
}
